import { PENDING_MAX_BUMP_TTL_MS } from "./apiRetry.js";
import {
  suggestMaxUpdatesForWorkout,
  type MaxSuggestion,
} from "./maxSuggest.js";
import {
  suggestMaxRegressionsForWorkout,
  type CurrentMax,
} from "./maxRegression.js";
import type { Workout } from "./schemas/workout.js";

// Post-save MAX-bump pill state — pure logic shared by web's
// `apps/web/src/workout/hooks/useMaxBumpCandidates.ts` and mobile's
// `apps/mobile/src/maxes/useMaxBumpCandidates.ts`. The React state
// machine (useState + useEffect for the auto-clear timeout) stays in
// each hook because it's React-bound; the data transitions live here
// so the two surfaces can't drift on what counts as a candidate or
// how the accept/clear flow trims the list.
//
// Lifted in 1.34.0 (ALIGNMENT_AUDIT item 3). Hook wrappers shrunk to
// "wire React state + clock to these helpers" — see each hook file.

// Bidirectional MAX (next/coaching.md §9). A post-save MAX prompt now
// flows in BOTH directions: the classic upward bump (a new best) and the
// downward revision (a stale MAX a maximal-effort set proves too high).
export type MaxBumpDirection = "raise" | "lower";

export type MaxBumpCandidate = {
  exerciseId: string;
  // The proposed value + provenance. For a 'lower' candidate this carries
  // the regression's suggestedKg/e1RM/fromSet so every existing reader
  // (`c.suggestion.suggestedKg`) works unchanged regardless of direction.
  suggestion: MaxSuggestion;
  // 'raise' (default; omitted by legacy web-built candidates) or 'lower'.
  direction?: MaxBumpDirection;
  // 'lower' only: the stale stored MAX being revised down + its age, for
  // the regression banner copy ("MAX sceso: {currentMaxKg} → …,
  // {weeksSinceMax} sett."). Absent on raises.
  regression?: { currentMaxKg: number; weeksSinceMax: number };
};

export type PendingMaxBumps = {
  workoutId: string;
  expiresAt: number; // ms since epoch
  candidates: MaxBumpCandidate[];
};

// Evaluate a just-saved workout against the user's current MAXes; return
// the candidate list bundled with a TTL deadline, or null if no
// suggestion fires. Pure function: no React, no clock side-effects
// beyond the Date.now() read for the expiry stamp (callers can mock
// it by injecting `now` if needed). The hook layer dispatches this
// output straight into setState.
export const computeMaxBumpCandidates = (params: {
  workout: Workout;
  exercisePatterns: Parameters<
    typeof suggestMaxUpdatesForWorkout
  >[0]["exercisePatterns"];
  currentMaxes: Record<string, number | null | undefined>;
  // Optional {valueKg, setAt} map enabling the DOWNWARD (regression)
  // direction (next/coaching.md §9). When omitted (e.g. web, which doesn't
  // do lower yet) only upward bumps are produced — behaviour-preserving.
  // `nowMs` anchors the regression staleness gate; defaults to `now`.
  currentMaxesDetailed?: Record<string, CurrentMax | null | undefined>;
  // Per-exercise best qualifying e1RM over the recent corroboration
  // window (excluding the just-saved workout). Gates the DOWNWARD
  // direction so a single light day can't lower a MAX below a recent
  // stronger session (Antenore 2026-06-26). Absent ⇒ no corroboration.
  recentBestE1RM?: Record<string, number>;
  workoutId: string;
  now?: number;
}): PendingMaxBumps | null => {
  const now = params.now ?? Date.now();

  // RAISE — the classic upward bump (unchanged).
  const raises = suggestMaxUpdatesForWorkout({
    workout: params.workout,
    exercisePatterns: params.exercisePatterns,
    currentMaxes: params.currentMaxes,
  });
  const byExercise = new Map<string, MaxBumpCandidate>();
  for (const [exerciseId, suggestion] of Object.entries(raises)) {
    byExercise.set(exerciseId, { exerciseId, suggestion, direction: "raise" });
  }

  // LOWER — the downward revision. Only when a detailed map is supplied.
  // Raise wins if an exercise somehow qualifies for both (a new best is
  // unambiguous; the two gates are in practice mutually exclusive).
  if (params.currentMaxesDetailed) {
    const regressions = suggestMaxRegressionsForWorkout({
      workout: params.workout,
      currentMaxes: params.currentMaxesDetailed,
      recentBestE1RM: params.recentBestE1RM,
      nowMs: now,
    });
    for (const [exerciseId, reg] of Object.entries(regressions)) {
      if (byExercise.has(exerciseId)) continue;
      byExercise.set(exerciseId, {
        exerciseId,
        // Reuse the MaxSuggestion shape so every `.suggestion` reader works.
        suggestion: {
          suggestedKg: reg.suggestedKg,
          e1RM: reg.e1RM,
          fromSet: reg.fromSet,
        },
        direction: "lower",
        regression: {
          currentMaxKg: reg.currentMaxKg,
          weeksSinceMax: reg.weeksSinceMax,
        },
      });
    }
  }

  const candidates = [...byExercise.values()];
  if (candidates.length === 0) return null;
  return {
    workoutId: params.workoutId,
    expiresAt: now + PENDING_MAX_BUMP_TTL_MS,
    candidates,
  };
};

// Drop one candidate from a pending block; collapse to null when the
// last one goes so the consumer can clear the pill. Pure setter the
// hook's `acceptCandidate` callback forwards to.
export const dropCandidateFromPending = (
  prev: PendingMaxBumps | null,
  exerciseId: string,
): PendingMaxBumps | null => {
  if (!prev) return null;
  const remaining = prev.candidates.filter(
    (c) => c.exerciseId !== exerciseId,
  );
  if (remaining.length === 0) return null;
  return { ...prev, candidates: remaining };
};
